import CloseIcon from '@mui/icons-material/Close'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogProps,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import {
  listTableBodyCellStyles,
  listTableEvenRowSx,
  listTableHeaderCellStyles,
  listTableSx,
} from 'components/list-table/list-table.styles'
import TranspotsErrorSnackbar from 'components/transpots-error-snackbar/transpots-error-snackbar.component'
import AceTripDialog from 'pages/trip/create-trip/ace-trip/ace-trip-dialog'
import { storedTripToAceFormValues } from 'pages/trip/create-trip/ace-trip/ace-trip-payload'
import { useListBorderConnectTripsQuery } from 'store/borderconnect/borderconnect.api'
import {
  BorderConnectDriver,
  BorderConnectTrailer,
  BorderConnectTrip,
  ManifestKind,
} from 'store/borderconnect/borderconnect.types'
import { getUserId } from 'utils/common'

type BorderConnectManifestsDialogProps = {
  handleClose: () => void
} & DialogProps

const HEADERS = [
  'TRIP NUMBER',
  'TYPE',
  'STATUS',
  'PORT',
  'DRIVER',
  'TRUCK',
  'TRAILER',
  'LAST UPDATED',
  'ACTIONS',
] as const

const HeaderCell = styled(TableCell)({
  ...listTableHeaderCellStyles,
})

const BodyCell = styled(TableCell)({
  ...listTableBodyCellStyles,
})

const toggleSx = (selected: boolean) => ({
  textTransform: 'none' as const,
  fontWeight: 700,
  borderRadius: 2,
  minWidth: 72,
  px: 2.5,
  color: selected ? '#1B7A4A' : '#2E7D57',
  bgcolor: selected ? '#D4F0DC' : 'common.white',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: selected ? '#C5E8D0' : 'grey.50',
  },
})

const formatList = (values: Array<string | undefined>) =>
  values.map((value) => value?.trim()).filter(Boolean).join(', ')

const driverLabel = (driver: BorderConnectDriver) =>
  [driver.firstName, driver.lastName].filter(Boolean).join(' ').trim() ||
  driver.driverNumber ||
  ''

const trailerLabel = (trailer: BorderConnectTrailer) =>
  trailer.number?.trim() ?? ''

const formatLastUpdated = (value?: string) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const tripKind = (trip: BorderConnectTrip): ManifestKind | null => {
  const sk = trip.SK?.toUpperCase() ?? ''
  if (sk.startsWith('ACE#')) return 'ACE'
  if (sk.startsWith('ACI#')) return 'ACI'
  const data = String(trip.data ?? '').toUpperCase()
  if (data.startsWith('ACE')) return 'ACE'
  if (data.startsWith('ACI')) return 'ACI'
  return null
}

const tripStatus = (trip: BorderConnectTrip) => {
  const raw = String(trip.status || trip.sendStatus || '').toUpperCase()
  if (raw === 'IMPORTED' || raw === 'ACCEPTED') {
    return { label: 'Accepted', color: '#2E7D32' }
  }
  if (raw === 'DATA_ERROR' || raw === 'ERROR' || raw === 'FAILED') {
    return { label: 'Error', color: '#D32F2F' }
  }
  if (raw === 'OK' || raw === 'SENT') {
    return { label: 'Sent', color: '#0288D1' }
  }
  return { label: 'Pending', color: '#ED6C02' }
}

const tripMatchesSearch = (trip: BorderConnectTrip, search: string) => {
  if (!search) return true
  const haystack = [
    trip.tripNumber,
    trip.SK,
    trip.sendStatus,
    trip.status,
    trip.usPortOfArrival,
    trip.portOfArrival,
    trip.truck?.number,
    ...(trip.drivers ?? []).map(driverLabel),
    ...(trip.trailers ?? []).map(trailerLabel),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(search)
}

const detailsTooltip = (trip: BorderConnectTrip) => (
  <Box sx={{ p: 0.25, maxWidth: 360 }}>
    <Typography variant="caption" display="block" fontWeight={700}>
      Message
    </Typography>
    <Typography
      variant="caption"
      display="block"
      sx={{ mb: 1, whiteSpace: 'pre-wrap' }}
    >
      {String(trip.message || '').trim() || '—'}
    </Typography>
    <Typography variant="caption" display="block" fontWeight={700}>
      Note
    </Typography>
    <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-wrap' }}>
      {String(trip.note || '').trim() || '—'}
    </Typography>
  </Box>
)

const BorderConnectManifestsDialog = ({
  open,
  handleClose,
}: BorderConnectManifestsDialogProps) => {
  const userId = getUserId()
  const [manifestKind, setManifestKind] = useState<ManifestKind>('ACE')
  const [search, setSearch] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [editingTrip, setEditingTrip] = useState<BorderConnectTrip | null>(null)

  const { data: trips = [], isFetching, isError, error, refetch } =
    useListBorderConnectTripsQuery(
      { userId },
      { skip: !open || !userId, refetchOnMountOrArgChange: true }
    )

  useEffect(() => {
    if (!open) return
    setManifestKind('ACE')
    setSearch('')
    setErrorMessage('')
    setEditingTrip(null)
    if (userId) {
      refetch()
    }
  }, [open, userId, refetch])

  useEffect(() => {
    if (!isError) {
      setErrorMessage('')
      return
    }
    const apiError = error as {
      data?: { error?: { message?: string }; message?: string }
    }
    setErrorMessage(
      apiError?.data?.error?.message ||
        apiError?.data?.message ||
        'Failed to load BorderConnect manifests'
    )
  }, [isError, error])

  const editFormValues = useMemo(
    () => (editingTrip ? storedTripToAceFormValues(editingTrip) : null),
    [editingTrip]
  )

  const filteredTrips = useMemo(() => {
    const term = search.trim().toLowerCase()
    return trips
      .filter((trip) => tripKind(trip) === manifestKind)
      .filter((trip) => tripMatchesSearch(trip, term))
  }, [trips, search, manifestKind])

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '95vw' },
            maxWidth: '95vw',
            height: { xs: '100%', sm: '95vh' },
            maxHeight: { xs: '100%', sm: '95vh' },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
            overflow: 'hidden',
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            disableRipple
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 20,
              color: 'error.main',
              width: 40,
              height: 40,
              bgcolor: 'common.white',
              boxShadow: 1,
              '&:hover': { backgroundColor: 'grey.100' },
              '&:active': { backgroundColor: 'grey.200' },
              '& .MuiTouchRipple-root': { display: 'none' },
            }}
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </IconButton>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              pt: 7,
              px: 3,
              pb: 3,
            }}
          >
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 2.5, pr: 6 }}
            >
              <Box>
                <Typography fontWeight={700} fontSize={22} lineHeight={1.2}>
                  Emanifest
                </Typography>
                <Typography color="text.secondary" fontSize={14} sx={{ mt: 0.5 }}>
                  Manage your ACE and ACI manifests sent through BorderConnect.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={isFetching}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 2,
                }}
              >
                {isFetching ? 'Refreshing…' : 'Refresh'}
              </Button>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <TextField
                placeholder="Search trip number, driver, truck, trailer..."
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 280, maxWidth: 460, flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 0.5,
                  bgcolor: 'common.white',
                  flexShrink: 0,
                }}
              >
                <Button
                  disableElevation
                  variant="contained"
                  onClick={() => setManifestKind('ACE')}
                  sx={toggleSx(manifestKind === 'ACE')}
                >
                  ACE
                </Button>
                <Button
                  disableElevation
                  variant="contained"
                  onClick={() => setManifestKind('ACI')}
                  sx={toggleSx(manifestKind === 'ACI')}
                >
                  ACI
                </Button>
              </Box>
            </Stack>

            <TableContainer sx={{ flex: 1, minHeight: 0 }}>
              <Table stickyHeader sx={listTableSx}>
                <TableHead>
                  <TableRow>
                    {HEADERS.map((header) => (
                      <HeaderCell key={header}>{header}</HeaderCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTrips.length === 0 ? (
                    <TableRow>
                      <BodyCell colSpan={HEADERS.length} sx={{ py: 3 }}>
                        {isFetching
                          ? `Loading ${manifestKind} eManifests...`
                          : `No ${manifestKind} eManifests found.`}
                      </BodyCell>
                    </TableRow>
                  ) : (
                    filteredTrips.map((trip) => {
                      const kind = tripKind(trip) ?? manifestKind
                      const status = tripStatus(trip)
                      return (
                        <TableRow
                          key={trip.SK || trip.tripNumber}
                          sx={listTableEvenRowSx}
                        >
                          <BodyCell>
                            <Typography
                              component="span"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 700,
                                fontSize: 14,
                              }}
                            >
                              {trip.tripNumber || trip.SK}
                            </Typography>
                          </BodyCell>
                          <BodyCell>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.25,
                                borderRadius: 999,
                                bgcolor: '#D4F0DC',
                                color: '#1B7A4A',
                                fontWeight: 700,
                                fontSize: 12,
                              }}
                            >
                              {kind}
                            </Box>
                          </BodyCell>
                          <BodyCell>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: status.color,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                fontSize={14}
                                fontWeight={600}
                                sx={{ color: status.color }}
                              >
                                {status.label}
                              </Typography>
                            </Stack>
                          </BodyCell>
                          <BodyCell>
                            {trip.usPortOfArrival || trip.portOfArrival || ''}
                          </BodyCell>
                          <BodyCell>
                            {formatList((trip.drivers ?? []).map(driverLabel))}
                          </BodyCell>
                          <BodyCell>{trip.truck?.number || ''}</BodyCell>
                          <BodyCell>
                            {formatList((trip.trailers ?? []).map(trailerLabel))}
                          </BodyCell>
                          <BodyCell>
                            {formatLastUpdated(
                              typeof trip.dateTime === 'string'
                                ? trip.dateTime
                                : trip.sentAt
                            )}
                          </BodyCell>
                          <BodyCell>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip
                                arrow
                                title={detailsTooltip(trip)}
                                enterDelay={200}
                              >
                                <IconButton
                                  size="small"
                                  aria-label="View details"
                                >
                                  <VisibilityOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit and resend" enterDelay={400}>
                                <IconButton
                                  size="small"
                                  aria-label="Edit"
                                  onClick={() => setEditingTrip(trip)}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </BodyCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
      </Dialog>
      <AceTripDialog
        open={Boolean(editingTrip)}
        mode="edit"
        initialValues={editFormValues}
        onClose={() => setEditingTrip(null)}
      />
      <TranspotsErrorSnackbar
        message={errorMessage}
        open={errorMessage !== ''}
        handleClose={() => setErrorMessage('')}
      />
    </>
  )
}

export default BorderConnectManifestsDialog
